<template>
  <div class="tw-w-full" ref="root">
    <label
      v-if="textLabel"
      class="tw-block tw-mb-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-500"
      :class="size === 'sm' ? 'tw-h-4 tw-leading-4' : ''"
    >
      {{ textLabel }}
    </label>

    <div class="tw-relative tw-w-full tw-min-w-0">
      <button
        ref="buttonEl"
        type="button"
        class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-border tw-border-slate-300 tw-text-left tw-shadow-sm tw-shadow-slate-900/5 tw-transition focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/15 focus:tw-border-indigo-500/55"
        :class="[
          size === 'sm' ? 'tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-sm' : 'tw-rounded-xl tw-px-3 tw-py-2',
          disabled
            ? 'tw-bg-slate-50 tw-border-slate-200 tw-text-slate-400 tw-cursor-not-allowed'
            : 'tw-bg-white/70 tw-border-slate-300/70 tw-text-slate-900 hover:tw-bg-white',
          isOpen && !disabled ? 'tw-ring-4 tw-ring-indigo-500/15 tw-border-indigo-500/55' : '',
        ]"
        :disabled="disabled"
        @click="toggle"
      >
        <span class="tw-flex-1 tw-min-w-0 tw-truncate" :class="selectedLabel ? 'tw-text-slate-900' : 'tw-text-slate-500'">
          {{ selectedLabel || placeholder || 'Select…' }}
        </span>

        <span class="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          <button
            v-if="clearable && !disabled && modelValue != null && modelValue !== ''"
            type="button"
            class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-text-slate-500 hover:tw-text-slate-700"
            aria-label="Clear"
            @click.stop="clear"
          >
            <font-awesome-icon icon="xmark" class="tw-h-4 tw-w-4" />
          </button>

          <font-awesome-icon
            icon="chevron-down"
            class="tw-h-4 tw-w-4 tw-text-slate-500 tw-transition-transform"
            :class="isOpen ? 'tw-rotate-180' : ''"
          />
        </span>
      </button>
      
      <!-- Focus lands here once a value is committed, so the popup closes without
         focus falling back to <body> and the next Tab reaches whatever follows
         this component in the form. tabindex="-1" keeps it out of the tab order
         itself, so it is never a stop the user has to press through. -->
      <span ref="afterChangeFocusEl" tabindex="-1" class="tw-outline-none" />

      <Teleport to="body">
        <div
          v-if="isOpen && !disabled"
          ref="menuEl"
          class="tw-fixed tw-z-[3000] tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-300/70 tw-bg-white tw-shadow-xl tw-shadow-slate-900/50"
          :style="menuStyle"
        >
          <div v-if="searchable" class="tw-p-2 tw-border-b tw-border-slate-200/70">
            <div class="tw-relative">
              <input
                ref="searchInput"
                v-model="query"
                type="text"
                class="tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 placeholder:tw-text-slate-400 focus:tw-outline-none focus:tw-ring-4 focus:tw-ring-indigo-500/25 focus:tw-border-indigo-500/65"
                :placeholder="selectedLabel || placeholder || 'Select…'"
                @keydown.down.prevent="moveActive(1)"
                @keydown.up.prevent="moveActive(-1)"
                @keydown.enter.prevent="selectActive()"
                @keydown.esc.prevent="close()"
              />
            </div>
          </div>

          <div class="tw-max-h-64 tw-overflow-auto tw-p-1">
            <button
              v-for="(opt, idx) in filteredOptions"
              :key="optionKey(opt, idx)"
              type="button"
              class="tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-transition"
              :class="[
                idx === activeIndex ? 'tw-text-slate-900 tw-bg-slate-900/20 ' : 'tw-text-slate-700 hover:tw-bg-slate-900/30',
              ]"
              @mouseenter="activeIndex = idx"
              @click="selectOption(opt)"
            >
              <span class="tw-truncate">{{ optionLabel(opt) }}</span>
              <font-awesome-icon
                v-if="isSelected(opt)"
                icon="check"
                class="tw-h-4 tw-w-4 tw-text-slate-700 tw-flex-shrink-0"
              />
            </button>

            <div v-if="filteredOptions.length === 0" class="tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500">
              No results
            </div>
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';

export default defineComponent({
  name: 'FilterableDropdown',
  props: {
    textLabel: String,
    modelValue: [String, Number, Object, Array, null],
    inPill: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: undefined,
    },
    customLabel: {
      type: Function,
      default: undefined,
    },
    reduce: {
      type: Function,
      default: undefined,
    },
    placeholder: {
      type: String,
      default: 'Select…',
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Takes keyboard focus on mount, without opening the menu. Set it on the
    // first field of a form or modal so the user can start tabbing from there.
    autofocus: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
  },
  // search and search-change are interchangeable/duplicates, should be merged into 'search-change'
  emits: ['update:modelValue', 'input', 'search-change'],
  data() {
    return {
      isOpen: false,
      query: '',
      activeIndex: 0,
      menuStyle: {
        top: '0px',
        left: '0px',
        width: 'auto',
      } as Record<string, string>,
    };
  },
  watch: {
    query(val: string) {
      (this as any).$emit('search-change', val);
    },
  },
  computed: {
    safeOptions(): any[] {
      return Array.isArray((this as any).options) ? ((this as any).options as any[]) : [];
    },
    filteredOptions(): any[] {
      const vm = this as any;
      const q = String(vm.query || '').trim().toLowerCase();
      if (!vm.searchable || !q) return vm.safeOptions;
      return (vm.safeOptions || []).filter((opt: any) => {
        return String(vm.optionLabel(opt) || '').toLowerCase().includes(q);
      });
    },
    selectedLabel(): string {
      const vm = this as any;
      const opts = vm.safeOptions || [];
      const selected = opts.find((opt: any) => vm.isSelected(opt));
      if (selected) return vm.optionLabel(selected);
      if (vm.modelValue && typeof vm.modelValue === 'object') {
        return vm.optionLabel(vm.modelValue);
      }
      if (vm.modelValue !== null && vm.modelValue !== undefined && vm.modelValue !== '') {
        return String(vm.modelValue);
      }
      return '';
    },
  },
  mounted() {
    document.addEventListener('click', (this as any).onDocumentClick, true);
    document.addEventListener('keydown', (this as any).onKeydown, true);
    window.addEventListener('resize', (this as any).repositionMenu, true);
    window.addEventListener('scroll', (this as any).repositionMenu, true);

    if ((this as any).autofocus && !(this as any).disabled) {
      nextTick(() => {
        (this as any).focusTrigger();
      });
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', (this as any).onDocumentClick, true);
    document.removeEventListener('keydown', (this as any).onKeydown, true);
    window.removeEventListener('resize', (this as any).repositionMenu, true);
    window.removeEventListener('scroll', (this as any).repositionMenu, true);
  },
  methods: {
    updateValue(val: any) {
      this.$emit('update:modelValue', val);
      this.$emit('input', val);
    },
    toggle() {
      const vm = this as any;
      if (vm.disabled) return;
      vm.isOpen = !vm.isOpen;
      if (vm.isOpen) {
        vm.query = '';
        vm.activeIndex = 0;
        vm.repositionMenu();
        if (vm.searchable) {
          nextTick(() => {
            const searchInput = vm.$refs.searchInput as HTMLElement | undefined;
            searchInput?.focus();
          });
        }
      }
    },
    close() {
      (this as any).isOpen = false;
      (this as any).query = '';
      (this as any).activeIndex = 0;
      this.focusTrigger();
    },
    repositionMenu() {
      const vm = this as any;
      if (!vm.isOpen) return;
      nextTick(() => {
        const buttonEl = vm.$refs.buttonEl as HTMLElement | undefined;
        if (!buttonEl) return;
        const buttonRect = buttonEl.getBoundingClientRect();
        const menuRect = (vm.$refs.menuEl as HTMLElement)?.getBoundingClientRect();

        /* Calculate dynamic maxHeight based on available viewport space:

          Updated the positioning logic to use the simpler formula:

          openUp = spaceAbove > spaceBelow (direct comparison without thresholds)
          maxHeight = Math.min(availableSpace, preferredMaxHeight) - margin
          The menu now opens in the direction with more space and caps height to fit within viewport.
          The menu will now never flow outside the visible window, regardless of opening direction.

        */
        const margin = 6;
        const preferredMaxHeight = 320;
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        // Determine which direction has more space
        // but first check if the menu height fits well enough below
        // we want to choose "below" as the default if there is enough space below
        const openUp = (menuRect.height > spaceBelow + 50) && (spaceAbove > spaceBelow);

        const availableSpace = openUp ? spaceAbove : spaceBelow;
        const maxHeight = Math.min(availableSpace, preferredMaxHeight) - margin;
        // if menu to open upwards,
        //    anchor the menu bottom edge at the button's top edge
        //    hence we need to go backwards from the bottom of the window going up 
        //    i.e. invert the bottom:  height - button's top edge position
        // else 
        //   use normal calculations, anchor the top menu edge to the button's bottom edge position
        const bottomOrTopPos = openUp ? (window.innerHeight - buttonRect.top + margin) : (buttonRect.bottom + margin);

        vm.menuStyle = {
          [openUp ? 'bottom' : 'top']: `${Math.round(bottomOrTopPos)}px`,
          left: `${Math.round(buttonRect.left)}px`,
          width: `${Math.round(buttonRect.width)}px`,
          maxHeight: `${Math.round(maxHeight)}px`,
        };
      });
    },
    clear() {
      this.updateValue(null);
      this.close();
    },
    optionKey(opt: any, idx: number) {
      if (opt && typeof opt === 'object' && 'id' in opt) return (opt as any).id;
      return idx;
    },
    optionLabel(opt: any): string {
      const vm = this as any;
      if (opt == null) return '';
      if (typeof opt === 'string' || typeof opt === 'number') return String(opt);

      if (vm.customLabel && typeof vm.customLabel === 'function') {
        try {
          return String(vm.customLabel(opt) ?? '');
        } catch {
          return '';
        }
      }

      const key = vm.label;
      if (key && opt && typeof opt === 'object' && key in opt) return String(opt[key]);
      if (opt && typeof opt === 'object' && 'name' in opt) return String(opt.name);
      return String(opt);
    },
    optionValue(opt: any) {
      const vm = this as any;
      if (vm.reduce && typeof vm.reduce === 'function') {
        return vm.reduce(opt);
      }
      return opt;
    },
    isSelected(opt: any): boolean {
      const vm = this as any;
      const v = vm.modelValue;
      const ov = vm.optionValue(opt);
      if (v === ov) return true;
      try {
        return JSON.stringify(v) === JSON.stringify(ov);
      } catch {
        return false;
      }
    },
    selectOption(opt: any) {
      const vm = this as any;
      vm.updateValue(vm.optionValue(opt));
      vm.close();
      vm.$nextTick(() => {
        this.focusTrigger();
      });
    },
    // Moves keyboard focus onto the closed dropdown's trigger button, without
    // opening the menu. Used by the `autofocus` prop, and callable by a parent
    // through a template ref.
    focusTrigger() {
      const buttonEl = this.$refs.buttonEl as HTMLElement | undefined;
      buttonEl?.focus();
    },
    // Parks focus on the sentinel that follows the input, so a committed value
    // does not drop focus onto <body> and send the next Tab back to the top of
    // the page.
    moveFocusPastPicker() {
      const focusEl = this.$refs.afterChangeFocusEl as HTMLElement | undefined;
      focusEl?.focus({ preventScroll: true });
    },
    moveActive(delta: number) {
      const vm = this as any;
      const max = (vm.filteredOptions || []).length;
      if (!max) return;
      vm.activeIndex = (vm.activeIndex + delta + max) % max;
    },
    selectActive() {
      const vm = this as any;
      const opt = (vm.filteredOptions || [])[vm.activeIndex];
      if (!opt) return;
      vm.selectOption(opt);
    },
    onDocumentClick(e: MouseEvent) {
      const vm = this as any;
      if (!vm.isOpen) return;
      const root = vm.$refs.root as HTMLElement | undefined;
      const menuEl = vm.$refs.menuEl as HTMLElement | undefined;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      if (menuEl && e.target instanceof Node && menuEl.contains(e.target)) return;
      vm.close();
    },
    onKeydown(e: KeyboardEvent) {
      const vm = this as any;
      if (!vm.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        vm.close();
      }
    },
  }
});
</script>
